import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import {
  agents,
  GROUP_ORDER,
  GROUP_META,
  STATUS_META,
  TIER_PRICE,
  recentlyAddedIds,
} from '../../data/agentMeta';

const { FiSearch, FiGrid, FiList, FiX, FiExternalLink, FiCheck, FiPlus } = FiIcons;

/* Lighten a hex identity color into a soft tinted background for the flat icon chip */
const tint = (hex, alpha = 0.12) => {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.planned;
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${m.cls}`}>
      {m.label}
    </span>
  );
};

const TierBadge = ({ agent }) => {
  if (agent.addonPriceUsd != null) {
    return (
      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800">
        {agent.minTier} or +${agent.addonPriceUsd} add-on
      </span>
    );
  }
  return (
    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary-50 text-primary-700">
      {agent.minTier} {TIER_PRICE[agent.minTier] ? `(${TIER_PRICE[agent.minTier]})` : ''}
    </span>
  );
};

const IconChip = ({ agent, size = 'lg' }) => {
  const dim = size === 'lg' ? 'w-11 h-11' : 'w-9 h-9';
  const ic = size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';
  return (
    <div
      className={`${dim} rounded-xl flex items-center justify-center flex-shrink-0`}
      style={{ backgroundColor: tint(agent.identityColor), color: agent.identityColor }}
    >
      <SafeIcon icon={agent.Icon} className={ic} />
    </div>
  );
};

const AgentCard = ({ agent, view, onOpen }) => {
  if (view === 'table') {
    return (
      <button
        onClick={() => onOpen(agent)}
        className="w-full text-left grid grid-cols-[auto_1fr_auto] sm:grid-cols-[auto_1.4fr_1fr_auto_auto] items-center gap-3 px-4 py-3 border-b border-gray-100 hover:bg-parchment-50 transition-colors"
        style={{ borderLeft: `3px solid ${agent.identityColor}` }}
      >
        <IconChip agent={agent} size="sm" />
        <div className="min-w-0">
          <div className="font-semibold text-gray-900 truncate">{agent.name}</div>
          <div className="text-xs text-gray-500 truncate">{agent.tagline}</div>
        </div>
        <div className="hidden sm:block text-xs text-gray-500">{agent.group}</div>
        <div className="hidden sm:block"><TierBadge agent={agent} /></div>
        <StatusBadge status={agent.status} />
      </button>
    );
  }
  return (
    <motion.button
      layout
      onClick={() => onOpen(agent)}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="text-left bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col"
      style={{ borderTop: `3px solid ${agent.identityColor}` }}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <IconChip agent={agent} />
        <StatusBadge status={agent.status} />
      </div>
      <h3 className="font-bold text-gray-900 mb-1 leading-snug">{agent.name}</h3>
      <p className="text-sm text-gray-600 leading-relaxed flex-1">{agent.tagline}</p>
      <div className="mt-4 flex items-center justify-between">
        <TierBadge agent={agent} />
        <span className="text-xs text-gray-400 font-medium">{GROUP_META[agent.group]?.short || agent.group}</span>
      </div>
    </motion.button>
  );
};

const AgentModal = ({ agent, onClose }) => (
  <AnimatePresence>
    {agent && (
      <motion.div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl relative max-h-[90vh] overflow-y-auto"
          initial={{ scale: 0.95, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          style={{ borderTop: `4px solid ${agent.identityColor}` }}
        >
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" aria-label="Close">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 mb-4">
            <IconChip agent={agent} />
            <div>
              <h3 className="text-xl font-bold text-gray-900 leading-tight">{agent.name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <StatusBadge status={agent.status} />
                <span className="text-xs text-gray-400">{agent.group}</span>
              </div>
            </div>
          </div>
          <p className="text-gray-700 leading-relaxed mb-5">{agent.description}</p>

          <div className="mb-5 text-sm">
            <div className="bg-parchment-50 rounded-lg p-3">
              <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Plan</div>
              <TierBadge agent={agent} />
            </div>
          </div>

          {agent.deps?.length > 0 && (
            <div className="mb-5">
              <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">Built on</div>
              <div className="flex flex-wrap gap-2">
                {agent.deps.map((d) => (
                  <span key={d} className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">{d}</span>
                ))}
              </div>
            </div>
          )}

          {agent.docUrl && (
            <a
              href={agent.docUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700"
            >
              Read docs <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
            </a>
          )}
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

/* Portable, registry-driven agent gallery: search + group filter + grid/table + detail modal */
const AgentsShowcase = ({ initialGroup = 'all' }) => {
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState(initialGroup);
  const [view, setView] = useState('grid');
  const [active, setActive] = useState(null);

  const groups = useMemo(
    () => GROUP_ORDER.filter((g) => agents.some((a) => a.group === g)),
    []
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return agents.filter((a) => {
      if (group === 'recent') {
        if (!recentlyAddedIds.includes(a.id)) return false;
      } else if (group !== 'all' && a.group !== group) {
        return false;
      }
      if (!q) return true;
      return (
        a.name.toLowerCase().includes(q) ||
        a.tagline.toLowerCase().includes(q) ||
        a.description.toLowerCase().includes(q) ||
        (a.deps || []).some((d) => d.toLowerCase().includes(q))
      );
    });
  }, [query, group]);

  const Chip = ({ value, label }) => (
    <button
      onClick={() => setGroup(value)}
      className={`px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
        group === value ? 'bg-primary-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-primary-300'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <SafeIcon icon={FiSearch} className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search agents, capabilities, providers…"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-300 text-sm"
            />
          </div>
          <div className="flex bg-white border border-gray-200 rounded-xl overflow-hidden flex-shrink-0">
            <button onClick={() => setView('grid')} aria-label="Grid view"
              className={`px-3 py-2.5 ${view === 'grid' ? 'bg-primary-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>
              <SafeIcon icon={FiGrid} className="w-4 h-4" />
            </button>
            <button onClick={() => setView('table')} aria-label="Table view"
              className={`px-3 py-2.5 ${view === 'table' ? 'bg-primary-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>
              <SafeIcon icon={FiList} className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          <Chip value="all" label={`All (${agents.length})`} />
          <Chip value="recent" label="Recently added" />
          {groups.map((g) => (
            <Chip key={g} value={g} label={GROUP_META[g]?.short || g} />
          ))}
        </div>
      </div>

      {/* Results */}
      {filtered.length === 0 ? (
        <p className="text-center text-gray-500 py-16">No agents match “{query}”.</p>
      ) : view === 'grid' ? (
        <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence>
            {filtered.map((a) => (
              <AgentCard key={a.id} agent={a} view="grid" onOpen={setActive} />
            ))}
          </AnimatePresence>
        </motion.div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
          <div className="hidden sm:grid grid-cols-[auto_1.4fr_1fr_auto_auto] gap-3 px-4 py-2.5 bg-parchment-50 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            <span className="w-9" />
            <span>Agent</span>
            <span>Area</span>
            <span>Plan</span>
            <span>Status</span>
          </div>
          {filtered.map((a) => (
            <AgentCard key={a.id} agent={a} view="table" onOpen={setActive} />
          ))}
        </div>
      )}

      <AgentModal agent={active} onClose={() => setActive(null)} />
    </div>
  );
};

export default AgentsShowcase;
